import { parsePhoneNumber } from "libphonenumber-js";
import { getAPI, postAPI } from "../api/callAPI";

/**
 * Parse full phone (E.164) to API format.
 * @param {string} phone - e.g. "+449876543210"
 * @returns {{ country_code: string, mobile_no: string }}
 */
export function parsePhoneForOtp(phone) {
  const p = parsePhoneNumber(phone || "");
  if (!p) throw new Error("Invalid phone number");
  return {
    country_code: `+${p.countryCallingCode}`,
    mobile_no: p.nationalNumber,
  };
}

/**
 * Get single store by ID.
 * API: GET /api/stores/{storeId}
 * Response: data?.data?.[0] = store object
 * @param {string} endpoint - Subdomain (e.g. "romeolane")
 * @param {string|number} storeId - Store ID
 * @returns {Promise<object|null>} Store object or null (null on failure / not found)
 */
export async function getSingleStore(endpoint, storeId) {
  if (!endpoint || storeId == null || storeId === "") return null;
  try {
    const data = await getAPI(endpoint, `stores/${storeId}`);
    const store = data?.data?.[0] ?? data?.data?.["0"] ?? null;
    return store || null;
  } catch (err) {
    console.error("getSingleStore error:", err);
    return null;
  }
}

/**
 * Fetch table order detail (get order ID for store + table).
 * API: POST /api/get-table-order-detail
 * Payload: { store_id: storeId, table_id: tableNumber }
 * Response: data.success === true and data.data present.
 * Order ID: data.data?.id || data.data?.order?.id || data.data?.order_id
 * @throws {Error} "No active order for this table" if !data.success or no data.data
 * @throws {Error} "Order ID not found in response" if no orderId in response
 */
export async function getTableOrderDetail(endpoint, storeId, tableNumber) {
  if (!endpoint || storeId == null || storeId === "" || tableNumber == null || tableNumber === "") {
    throw new Error("Store ID and table number are required");
  }
  const data = await postAPI(endpoint, "get-table-order-detail", {
    store_id: String(storeId),
    table_id: String(tableNumber),
  });
  if (!data?.success || data?.data == null) {
    throw new Error("No active order for this table");
  }
  const orderId = data.data?.id ?? data.data?.order?.id ?? data.data?.order_id;
  if (orderId == null || orderId === "") {
    throw new Error("Order ID not found in response");
  }
  return orderId;
}

/**
 * Get order by ID.
 * Response shape: { success: true, data: { order: { ... } } }
 * @param {string} endpoint - Subdomain
 * @param {string|number} orderId - Order ID
 * @returns {Promise<object|null>} Order object or null
 */
export async function getOrder(endpoint, orderId) {
  if (!endpoint || orderId == null || orderId === "") return null;
  try {
    const data = await getAPI(endpoint, `orders/${orderId}`);
    if (data?.success && data?.data?.order) {
      return data.data.order;
    }
    return null;
  } catch (err) {
    console.error("getOrder error:", err);
    throw err;
  }
}

/**
 * Send OTP to mobile number.
 * API: POST /api/mobile-otp
 * Payload: { mobile_no, country_code, otp_type: "send" }
 * @param {string} endpoint - Subdomain
 * @param {object} params - { country_code: "+44", mobile_no: "9876543210" }
 * @returns {Promise<{ success: boolean }>}
 * @throws {Error} with message from API or "Error sending OTP"
 */
export async function sendMobileOtp(endpoint, params) {
  const { country_code, mobile_no } = params || {};
  if (!endpoint || !country_code || !mobile_no) {
    throw new Error("Country code and mobile number are required");
  }
  const data = await postAPI(endpoint, "mobile-otp", {
    mobile_no: String(mobile_no),
    country_code: String(country_code),
    otp_type: "send",
  });
  if (!data?.success) {
    throw new Error(data?.message || data?.data?.message || "Error sending OTP");
  }
  return { success: true };
}

/**
 * Verify OTP entered by user.
 * API: POST /api/mobile-verify-otp
 * Payload: { mobile_no, country_code, otp }
 * @param {string} endpoint - Subdomain
 * @param {object} params - { country_code, mobile_no, otp }
 * @returns {Promise<{ success: boolean, token?: string, user?: object }>}
 * @throws {Error} with message from API or "Invalid OTP"
 */
export async function verifyMobileOtp(endpoint, params) {
  const { country_code, mobile_no, otp } = params || {};
  if (!endpoint || !country_code || !mobile_no || otp == null || otp === "") {
    throw new Error("Mobile number and OTP are required");
  }
  const data = await postAPI(endpoint, "mobile-verify-otp", {
    mobile_no: String(mobile_no),
    country_code: String(country_code),
    otp: String(otp),
    came_from: 'Web',
  });
  if (!data?.success) {
    throw new Error(data?.message || data?.data?.message || "Invalid OTP");
  }
  return {
    success: true,
    token: data?.data?.token,
    user: data?.data?.user,
  };
}

// --- Stripe (same contract as feedthetummy backend) ---

/**
 * Get Stripe publishable key for the store.
 * API: GET /api/get-store-payment-config/:storeId
 * @param {string} endpoint - Subdomain
 * @param {string|number} storeId - Store ID
 * @returns {Promise<string>} data.public_payment_key
 */
export async function getStorePaymentConfig(endpoint, storeId) {
  if (!endpoint || storeId == null || storeId === "") {
    throw new Error("Endpoint and store ID are required");
  }
  const data = await getAPI(endpoint, `get-store-payment-config/${storeId}`);
  const key = data?.data?.public_payment_key;
  if (!key) throw new Error(data?.message || "Missing Stripe publishable key");
  return key;
}

/**
 * Create a Stripe PaymentIntent for the order.
 * API: POST /api/payment
 * @param {string} endpoint - Subdomain
 * @param {string|number} orderId - Order ID
 * @param {boolean} [capture=false] - Capture immediately or authorize only
 * @returns {Promise<string>} data.client_secret
 */
export async function createPaymentIntent(endpoint, orderId, capture = false) {
  if (!endpoint || orderId == null || orderId === "") {
    throw new Error("Endpoint and order ID are required");
  }
  const data = await postAPI(endpoint, "payment", {
    order_id: Number(orderId) || String(orderId),
    capture: !!capture,
  });
  if (!data?.success || !data?.data?.client_secret) {
    throw new Error(data?.message || "Failed to create PaymentIntent");
  }
  return data.data.client_secret;
}

/**
 * Mark order as paid after successful Stripe payment.
 * API: POST /api/orders-status-update
 * @param {string} endpoint - Subdomain
 * @param {string|number} orderId - Order ID
 * @param {object} [options]
 * @param {string} [options.payment_type='card']
 * @param {number} [options.order_status=1]
 * @param {number} [options.payment_status=1]
 */
export async function updateOrderStatus(endpoint, orderId, options = {}) {
  if (!endpoint || orderId == null || orderId === "") {
    throw new Error("Endpoint and order ID are required");
  }
  const data = await postAPI(endpoint, "orders-status-update", {
    order_id: Number(orderId) || String(orderId),
    payment_type: options.payment_type ?? "card",
    order_status: options.order_status ?? 1,
    payment_status: options.payment_status ?? 1,
  });
  if (!data?.success) {
    throw new Error(data?.message || "Failed to update order status");
  }
  return data;
}
